import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { cn } from '../../lib/utils';

ChartJS.register(ArcElement, Tooltip, Legend);

export interface DonutLegendItem {
  label: string;
  /** Slice size, 0–100. */
  percentage: number;
  color: string;
}

export interface DonutWithLegendProps {
  items: DonutLegendItem[];
  /** Unique DOM id for the external tooltip element (one per chart usage). */
  tooltipId: string;
  /**
   * Height class for the donut area. Defaults to 149px on mobile and 240px
   * on desktop (sm+) to match the dashboard design. Pass this in only if a
   * caller needs to override the default sizing.
   */
  heightClass?: string;
  /** Max-width class for the legend block. Default caps at the dashboard width. */
  legendMaxWidthClass?: string;
  /** Max-height class for the legend block (it scrolls beyond this). */
  legendMaxHeightClass: string;
}

/**
 * The dashboard donut + scrollable wrap legend (RestaurantRevenuePieChart /
 * SpendByCategoryDonut design) as a reusable presentational block. Callers supply
 * pre-translated labels, percentages and colors; empty states stay with the caller.
 *
 * Mobile: donut + legend sit side-by-side, donut fixed at 149x149.
 * Desktop (sm+): donut stacked on top of the legend, donut fixed at 240x240 (unchanged).
 */
export const DonutWithLegend: React.FC<DonutWithLegendProps> = ({
  items,
  tooltipId,
  heightClass,
  legendMaxWidthClass,
  legendMaxHeightClass,
}) => {
  const colors = items.map((i) => i.color);

  const chartData: ChartData<'doughnut'> = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => i.percentage),
        backgroundColor: colors,
        borderWidth: 0,
        borderRadius: 4,
        spacing: 6,
        hoverOffset: 2,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          let tooltipEl = document.getElementById(tooltipId);

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = tooltipId;
            tooltipEl.innerHTML = `
              <div class="bg-white border border-[#F5F5F5] rounded-lg p-1 text-[12px] shadow-lg">
                <span class="tooltip-text"></span>
              </div>
            `;
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          if (tooltipModel.body) {
            const textSpan = tooltipEl.querySelector('.tooltip-text') as HTMLElement;
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const item = items[dataIndex];
            textSpan.innerHTML = `${item.label} ${item.percentage.toFixed(1)}%`;
            textSpan.style.color = colors[dataIndex];
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX - 30 + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 40 + 'px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.zIndex = '100';
          tooltipEl.style.transition = 'all 0.1s ease';
          tooltipEl.style.transform = 'translateX(-50%)';
        },
      },
    },
  };

  return (
    <div className="w-full flex sm:flex-col gap-4 flex-row items-center">
      <div
        className={cn(
          'relative flex-shrink-0 flex items-center justify-center select-none',
          'w-[149px] h-[149px] sm:w-[240px] sm:h-[240px] sm:pt-4',
          heightClass
        )}
      >
        <Doughnut data={chartData} options={options} />
      </div>

      <div
        className={cn(
          'mt-0 sm:mt-4 sm:px-3 space-y-3 sm:space-y-4 flex-1 min-w-0 sm:w-full overflow-y-auto custom-scrollbar',
          legendMaxWidthClass,
          legendMaxHeightClass
        )}
      >
        {items.map((item, index) => (
          <div key={index} className="w-full flex items-center justify-between gap-3">
            <div className="flex items-center gap-[5px] min-w-0">
              <div
                className="w-3 h-3 rounded-[3px] flex-shrink-0"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-[14px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC] truncate">
                {item.label}
              </span>
            </div>
            <span className="text-[#101828] dark:text-[#F8FAFC] text-[14px] leading-5 font-semibold whitespace-nowrap flex-shrink-0">
              {item.percentage.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DonutWithLegend;